import type { TFunction } from 'i18next';
import { fieldDetailMessage, OrgAdminApiError, type OrgMoveBlocker } from '@/api/orgAdmin';

/**
 * EAA-1891: Vietnamese for the cross-tree move's server verdicts.
 *
 * Kept OUT of `console/hierarchy/dragRules.ts` on purpose, even though that file
 * already holds `translateParentRuleMessage`. That table maps the ENGLISH literals
 * `PATCH /organizations/{id}` returns under `details.parent`, by prefix — a
 * mapping that is fragile by construction (the comment there says so: three
 * server literals share the "Unit type" prefix and the order is load-bearing).
 *
 * The move endpoints do better and this module reflects it: `move-preview` returns
 * a STABLE `rule` key per blocker beside the English message, so the translation
 * is a lookup on an enum rather than a prefix race. Introducing that keyed table
 * into the prefix table's file would invite the two to be confused for each
 * other; they answer different questions from different payloads.
 */

const VALIDATION_ERROR_CODE = 2000;
const PERMISSION_DENIED_CODE = 1009;

/** The keyed blocker table — `move-preview`'s `blockers[].rule`. */
export function translateMoveBlocker(t: TFunction, blocker: OrgMoveBlocker): string {
    switch (blocker.rule) {
        case 'cycle':
            return t('admin.organizations.move.ruleCycle');
        case 'inactive_parent':
            return t('admin.organizations.move.ruleInactiveParent');
        case 'unit_type_containment':
            return t('admin.organizations.move.ruleUnitTypeContainment');
        case 'unknown_unit_type':
            return t('admin.organizations.move.ruleUnknownUnitType');
        default:
            // A rule the server grew after this build shipped. Showing the raw
            // English beats swallowing it — an unmapped blocker the admin cannot
            // read is still better than a blocked button with no reason at all.
            return blocker.message;
    }
}

/**
 * `POST /organizations/{id}/move` failure → one Vietnamese line.
 *
 * The write path reports rule failures as English strings under
 * `details.parent_id` (not `parent` — a different key from the PATCH path, since
 * that is the field name in `OrganizationMoveSerializer`). There is no `rule` key
 * on this payload, so this falls back to the server text rather than guessing:
 * every rule the write can fail was already graded by the preview and shown in
 * the blockers panel, so reaching here means the state changed under the admin —
 * a race worth reporting verbatim.
 */
export function describeMoveFailure(t: TFunction, error: unknown): string {
    const nothingChanged = t('admin.organizations.move.nothingChangedSuffix');
    if (!(error instanceof OrgAdminApiError)) {
        return t('admin.organizations.move.connectionError');
    }
    if (error.code === PERMISSION_DENIED_CODE) {
        return t('admin.organizations.move.permissionDeniedError');
    }
    if (error.code === VALIDATION_ERROR_CODE) {
        const detail = fieldDetailMessage(error.details, 'parent_id');
        if (detail) return `${detail} ${nothingChanged}`;
    }
    return `${error.message} ${nothingChanged}`;
}
